import React from 'react';
import { useTheme } from '@/context/ThemeContext';
import { motion } from 'framer-motion';
import { TYPEFORM_URL } from '@/data/mock';
import {
  SectionHeading, StatCard, CTAButton, FeatureCard, AudienceCard
} from '@/components/shared/SharedComponents';
import {
  Dumbbell, Brain, Apple, Moon, ArrowRight, Check,
  Activity, TrendingUp, Droplets, Zap, Heart,
  Building2, ShieldCheck, Factory, Landmark, Store,
  Laptop, HeartPulse, GraduationCap, Plane, BarChart3, Lightbulb
} from 'lucide-react';

const PILLARS = [
  { icon: Dumbbell, title: 'Salud Física', description: 'Retos de actividad, pasos, ejercicio y movimiento diario adaptados a todos los niveles.' },
  { icon: Brain, title: 'Salud Mental', description: 'Meditación, mindfulness, gestión del estrés y herramientas de bienestar emocional.' },
  { icon: Apple, title: 'Alimentación', description: 'Nutrición inteligente, registro de comidas, recetas saludables y planes alimentarios.' },
  { icon: Moon, title: 'Sueño y Descanso', description: 'Monitoreo de calidad del sueño, rutinas nocturnas y técnicas de recuperación.' },
];

const STEPS = [
  { num: '01', title: 'Únete a Retos', desc: 'Retos de bienestar físico, mental y descanso' },
  { num: '02', title: 'Registra Actividad', desc: 'Actividad, hábitos, sueño y alimentación' },
  { num: '03', title: 'Mide Progreso', desc: 'La app mide el progreso en tiempo real' },
  { num: '04', title: 'Gana Puntos', desc: 'Cashback y recompensas por constancia' },
];

const AI_FEATURES = [
  { icon: Apple, title: 'Nutrición Inteligente', description: 'Registra comidas y estima calorías automáticamente.' },
  { icon: Lightbulb, title: 'Insights Personalizados', description: 'Identifica patrones y muestra avances.' },
  { icon: BarChart3, title: 'Monitoreo Continuo', description: 'Entiende el progreso y mejora hábitos.' },
];

const CHALLENGES = [
  { icon: Activity, title: 'Actividad física', image: 'https://images.pexels.com/photos/4943918/pexels-photo-4943918.jpeg?w=400&h=300&fit=crop' },
  { icon: TrendingUp, title: 'Movimiento y pasos', image: 'https://images.unsplash.com/photo-1552196527-bffef41ef674?w=400&h=300&fit=crop' },
  { icon: Droplets, title: 'Hidratación', image: 'https://images.unsplash.com/photo-1649345867132-e8bd35bedf76?w=400&h=300&fit=crop' },
  { icon: Apple, title: 'Alimentación saludable', image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&h=300&fit=crop' },
  { icon: Brain, title: 'Salud mental', image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=300&fit=crop' },
  { icon: Moon, title: 'Calidad del sueño', image: 'https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?w=400&h=300&fit=crop' },
];

const DEVICES = ['Apple Watch', 'Fitbit', 'Google Fit', 'Samsung Health', 'Garmin', 'Huawei Health', 'Polar', 'Xiaomi Mi Band'];

const REWARDS = [
  { item: 'Tarjeta regalo Amazon', points: '5,000 pts' },
  { item: 'Suscripción Gym Premium', points: '8,000 pts' },
  { item: 'Cupón Supermercado', points: '3,000 pts' },
  { item: 'Servicios digitales', points: '2,500 pts' },
];

const STATS = [
  { stat: '+72', description: 'NPS promedio entre usuarios activos' },
  { stat: '+65%', description: 'Adherencia mensual al programa' },
  { stat: '-30%', description: 'Reducción en ausentismo laboral' },
  { stat: '+5,000', description: 'Horas de contenido de bienestar disponible' },
];

const INDUSTRIES = [
  { icon: Building2, title: 'Corporativos y grandes empresas', description: 'Organizaciones con cientos o miles de colaboradores que buscan reducir el ausentismo y potenciar la productividad.' },
  { icon: ShieldCheck, title: 'Aseguradoras y EPS', description: 'Compañías de seguros que desean implementar programas de prevención, reduciendo costos de siniestralidad.' },
  { icon: Factory, title: 'Manufactura e industria', description: 'Plantas donde el bienestar físico del personal es crítico. Motiva a equipos operativos a mantenerse activos.' },
  { icon: Landmark, title: 'Banca y servicios financieros', description: 'Sector con altos niveles de estrés laboral donde el bienestar integral es clave para retener talento.' },
  { icon: Store, title: 'Retail y servicios', description: 'Cadenas con alta rotación de personal. Crea sentido de pertenencia y reduce el turnover con bienestar accesible.' },
  { icon: Laptop, title: 'Tecnología y startups', description: 'Empresas tech con equipos remotos que necesitan mantener a sus colaboradores conectados con su salud.' },
  { icon: HeartPulse, title: 'Salud y hospitales', description: 'Personal sometido a altas cargas laborales. Promueve el autocuidado previniendo el burnout.' },
  { icon: GraduationCap, title: 'Educación y universidades', description: 'Instituciones que quieren promover hábitos saludables entre docentes, administrativos y estudiantes.' },
  { icon: Plane, title: 'Cualquier industria', description: 'FitRewards es 100% configurable. Sin importar tu sector, adaptamos el programa a tu cultura y objetivos.' },
];

export default function FitRewardsPage() {
  const { currentTheme: t, theme } = useTheme();

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Hero */}
      <section className="relative pt-28 lg:pt-36 pb-16 lg:pb-24 overflow-hidden" style={{ background: t.heroBg }}>
        {theme === 'aurora' && (
          <>
            <div className="absolute top-20 right-20 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(58,113,181,0.4), transparent)' }} />
            <div className="absolute bottom-0 left-10 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(240,88,100,0.3), transparent)' }} />
          </>
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium mb-6"
                style={{ background: t.badgeBg, color: t.badgeText }}
              >
                +5,000 horas de contenido
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-3xl lg:text-5xl font-bold leading-tight mb-6"
                style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}
              >
                FitRewards: la app de bienestar corporativo que convierte hábitos saludables en recompensas reales
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-base lg:text-lg leading-relaxed mb-8"
                style={{ color: t.textSecondary }}
              >
                Impulsa hábitos saludables mediante retos, inteligencia artificial y un sistema de incentivos que realmente motiva.
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="flex flex-wrap gap-4">
                <CTAButton href={TYPEFORM_URL} />
                <CTAButton text="Conoce más" variant="secondary" onClick={() => document.getElementById('fitrewards-como')?.scrollIntoView({ behavior: 'smooth' })} />
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }} className="flex justify-center">
              <div className="flex justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_motivy-light/artifacts/dil3lxd4_sin_fondo_fit.png"
                  alt="FitRewards App - Resumen diario de bienestar"
                  className="max-w-[280px] lg:max-w-[340px]"
                  style={{
                    display: 'block',
                    filter: theme === 'light'
                      ? 'drop-shadow(0 20px 40px rgba(0,0,0,0.25))'
                      : 'drop-shadow(0 10px 30px rgba(58,113,181,0.35)) drop-shadow(0 30px 60px rgba(240,88,100,0.15))',
                  }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-16 lg:py-24" style={{ background: t.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading heading="+5,000 horas de contenido en 4 pilares" subtitle="FitRewards motiva a colaboradores y afiliados a adoptar hábitos saludables de forma constante y sostenible." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p, i) => <FeatureCard key={i} icon={p.icon} title={p.title} description={p.description} index={i} />)}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="fitrewards-como" className="py-16 lg:py-24" style={{ background: t.sectionAltBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading heading="Pequeñas acciones diarias, grandes cambios sostenibles" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-6 rounded-xl border"
                style={{ background: t.cardBg, borderColor: t.cardBorder }}
              >
                <div className="text-4xl font-extrabold mb-3" style={{ color: t.accent, fontFamily: 'Manrope, sans-serif' }}>{s.num}</div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>{s.title}</h3>
                <p className="text-sm" style={{ color: t.textSecondary }}>{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI */}
      <section className="py-16 lg:py-24" style={{ background: t.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading heading="Inteligencia Artificial aplicada al bienestar" subtitle="FitRewards incorpora IA para acompañar a cada usuario en su proceso de cambio de hábitos." />
          <div className="grid md:grid-cols-3 gap-6">
            {AI_FEATURES.map((f, i) => <FeatureCard key={i} icon={f.icon} title={f.title} description={f.description} index={i} />)}
          </div>
        </div>
      </section>

      {/* Streaks */}
      <section className="py-16 lg:py-24" style={{ background: t.sectionAltBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionHeading heading="Sistema de Rachas" subtitle="La constancia se convierte en motivación. FitRewards refuerza la creación de hábitos mediante un sistema de rachas que premia la continuidad." centered={false} />
              <ul className="space-y-3 mt-6">
                {['Mantiene su racha activa', 'Gana más puntos', 'Refuerza su compromiso personal'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: t.featureIconBg }}>
                      <Check size={14} style={{ color: t.featureIconColor }} />
                    </div>
                    <span className="text-sm" style={{ color: t.textSecondary }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-center">
              <div className="w-48 h-48 rounded-full flex items-center justify-center" style={{ background: t.featureIconBg }}>
                <span className="text-3xl font-bold" style={{ color: t.accent, fontFamily: 'Manrope, sans-serif' }}>+50 pts</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="py-16 lg:py-24" style={{ background: t.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading heading="Retos Saludables" subtitle="Metas alcanzables que generan cambios reales." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CHALLENGES.map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-xl overflow-hidden border"
                style={{ borderColor: t.cardBorder }}
              >
                <img src={c.image} alt={c.title} className="w-full h-40 object-cover" loading="lazy" />
                <div className="p-4 flex items-center gap-3" style={{ background: t.cardBg }}>
                  <c.icon size={18} style={{ color: t.featureIconColor }} />
                  <span className="text-sm font-medium" style={{ color: t.text }}>{c.title}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rewards */}
      <section className="py-16 lg:py-24" style={{ background: t.sectionAltBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading heading="Cuidarte también tiene recompensa" subtitle="FitRewards integra incentivos que convierten el esfuerzo en beneficios tangibles." />
          <div className="grid sm:grid-cols-2 gap-6 mb-10">
            <div className="p-6 rounded-xl border" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
              <Zap size={22} className="mb-3" style={{ color: t.featureIconColor }} />
              <h3 className="text-lg font-semibold mb-2" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>Incentivo directo</h3>
              <p className="text-sm" style={{ color: t.textSecondary }}>Motivador constante para mantener hábitos</p>
            </div>
            <div className="p-6 rounded-xl border" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
              <Heart size={22} className="mb-3" style={{ color: t.featureIconColor }} />
              <h3 className="text-lg font-semibold mb-2" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>Beneficios Salud</h3>
              <p className="text-sm" style={{ color: t.textSecondary }}>Gimnasios, productos deportivos y bienestar</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {REWARDS.map((r, i) => (
              <div key={i} className="p-4 rounded-xl border text-center" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
                <p className="text-sm font-medium mb-1" style={{ color: t.text }}>{r.item}</p>
                <p className="text-xs font-bold" style={{ color: t.accent }}>{r.points}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Devices */}
      <section className="py-16 lg:py-24" style={{ background: t.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading heading="Integración con dispositivos" subtitle="Conecta tu bienestar con tu tecnología" />
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-6 items-center justify-items-center">
            {[
              { name: 'Apple Watch', logo: 'https://cdn.simpleicons.org/apple/000000' },
              { name: 'Fitbit', logo: 'https://cdn.simpleicons.org/fitbit/00B0B9' },
              { name: 'Google Fit', logo: 'https://cdn.simpleicons.org/google/4285F4' },
              { name: 'Samsung Health', logo: 'https://cdn.simpleicons.org/samsung/1428A0' },
              { name: 'Garmin', logo: 'https://cdn.simpleicons.org/garmin/000000' },
              { name: 'Strava', logo: 'https://cdn.simpleicons.org/strava/FC4C02' },
              { name: 'Polar', logo: 'https://cdn.simpleicons.org/polarelectro/D32F2F' },
              { name: 'Huawei Health', logo: 'https://cdn.simpleicons.org/huawei/FF0000' },
            ].map((device, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="flex flex-col items-center gap-3 p-4 rounded-xl w-full transition-all duration-200"
                style={{ minHeight: 100 }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <div className="w-12 h-12 flex items-center justify-center">
                  <img
                    src={device.logo}
                    alt={device.name}
                    className="max-w-full max-h-full object-contain"
                    style={{ filter: theme !== 'light' ? 'brightness(0) invert(1)' : 'none', opacity: theme === 'light' ? 0.7 : 0.9 }}
                    loading="lazy"
                  />
                </div>
                <span className="text-xs font-medium text-center" style={{ color: t.textMuted }}>{device.name}</span>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-sm mt-10" style={{ color: t.textMuted }}>
            Nos integramos con más de 20 apps de salud y relojes inteligentes
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 lg:py-24" style={{ background: t.sectionAltBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading heading="Impacto y resultados" subtitle="Resultados reales de empresas que usan FitRewards." />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s, i) => <StatCard key={i} {...s} index={i} />)}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 lg:py-24" style={{ background: t.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading heading="¿Para quién es?" subtitle="FitRewards se adapta a cualquier industria y tamaño de organización." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {INDUSTRIES.map((ind, i) => <AudienceCard key={i} icon={ind.icon} title={ind.title} description={ind.description} index={i} />)}
          </div>
        </div>
      </section>

      {/* Enterprise */}
      <section className="py-16 lg:py-24" style={{ background: t.sectionAltBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 rounded-xl border" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
              <h3 className="text-2xl font-bold mb-4" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>Fácil de implementar</h3>
              <p className="text-sm mb-6" style={{ color: t.textSecondary }}>Se integra con sistemas empresariales y aseguradoras.</p>
              <ul className="space-y-3 mb-6">
                {['Programas a gran escala', 'Gestión de colaboradores', 'Métricas y resultados', 'Estrategias de prevención'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check size={14} style={{ color: t.featureIconColor }} />
                    <span className="text-sm" style={{ color: t.textSecondary }}>{item}</span>
                  </li>
                ))}
              </ul>
              <CTAButton text="Ver solución empresarial" href={TYPEFORM_URL} variant="secondary" />
            </div>
            <div className="p-8 rounded-xl border" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
              <h3 className="text-2xl font-bold mb-4" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>App de marca blanca</h3>
              <p className="text-sm mb-4" style={{ color: t.textSecondary }}>FitRewards como app personalizada, adaptando identidad visual y experiencia.</p>
              <p className="text-sm mb-6" style={{ color: t.textMuted }}>Fortalece tu marca y la relación con colaboradores.</p>
              <CTAButton text="Solicitar personalización" href={TYPEFORM_URL} />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24" style={{ background: t.bg }}>
        <div className="max-w-3xl mx-auto text-center px-4">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>¿Listo para transformar el bienestar de tu equipo?</h2>
          <CTAButton text="Agenda una demo personalizada" href={TYPEFORM_URL} />
        </div>
      </section>
    </div>
  );
}
